import React, { useEffect, useRef, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import { useStateCtx } from '../context/appContext';
import {
  NumberCell,
  handleDeleteConfirm,
  handlePortfolioSave,
  toggleDecayColumns,
} from '../utils/tableHandlers';
import { fetchTradeById, fetchTrades } from '../services/tradesServices';
import { useAuth } from '../context/auth';
import {
  ConfirmationModalContent,
  getTradeStatus,
} from '../utils/tableHelpers';
import { addCalculatedFieldsToAssignedTrades, calculateAssignedTradesFooter, calculateFinalPL, calculateLeftOffset, calculateTradeSums, calculateWidth, formatNumber } from '../utils/mathHelpers';
import { BsQuestionOctagon } from 'react-icons/bs';
import { guidedAssignedStocks, guidedPortfolio } from '../constants/guidedTutorialData';
import { guidedTutorialHandler } from '../utils/GuidedTutorialFun';
import { createNewPortfolioTrade } from '../constants/newRowsData';
import TableFooter from '../components/Table/TableFooter';
import TableBody from '../components/Table/TableBody';
import TableHeader from '../components/Table/TableHeader';
import TableActionControls from '../components/TableToolBar/TableActionControls';
import SearchAndColumnToggle from '../components/TableToolBar/SearchAndColumnToggle';
import { AssignedStockColumns } from '../constants/AssignedStockColumns';
import { fetchAssignedTrades } from '../services/assignedServices';


function AssignedStock() {
  // CONTEXT
  const { token, logout, visitedPages, markPageAsVisited, visitedPagesLoading } = useAuth();
  const { screenSize, activeMenu, currentColor } = useStateCtx();
  // STATES
  const [data, setData] = useState([]); // use this as your data source
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [contextMenuState, setContextMenuState] = useState(false);
  const [colorsKeyMapState, setColorsKeyMapState] = useState(false)
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [sort, setSort] = useState(true);
  const [newRow, setNewRow] = useState(false);
  const [footerSums, setFooterSums] = useState({
    assignedValue: 0,
    marketValue: 0,
    currentPL: 0,
    potentialProfit: 0,
  });

  const pageName = 'AssignedStock'; // Define a unique name for this page

  useEffect(() => {
    if (!visitedPagesLoading && token) {
      // Check if the page has not been visited and trigger the tutorial
      if (!visitedPages[pageName] || visitedPages[pageName] === false) {

        guidedTutorialHandler(guidedAssignedStocks);
        markPageAsVisited(pageName); // Mark the page as visited
      }
    }
  }, [visitedPages, markPageAsVisited, pageName, visitedPagesLoading]); // Dependencies for useEffect


  useEffect(() => {
    const sums = calculateAssignedTradesFooter(data);
    setFooterSums({
      assignedValue: sums.assignedValueSum,
      marketValue: sums.marketValueSum,
      currentPL: sums.currentPLSum,
      potentialProfit: sums.potentialProfitSum,
    });
  }, [data]);



  const handleDelete = (trade) => {
    setModalState({ isOpen: true, trade });
  };
  const useSkipper = () => {
    const shouldSkipRef = React.useRef(true);
    const shouldSkip = shouldSkipRef.current;

    // Wrap a function with this to skip a pagination reset temporarily
    const skip = React.useCallback(() => {
      shouldSkipRef.current = false;
    }, []);

    React.useEffect(() => {
      shouldSkipRef.current = true;
    });

    return [shouldSkip, skip];
  };
  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();

  const updateData = ({ rowIndex, rowId, columnId, value }) => {
    // Skip page index reset until after next rerender
    skipAutoResetPageIndex();
    try {
      // Function to fetch updated trade data and apply it to the row
      const updateRowWithFetchedData = async () => {
        const updatedTrade = await fetchTradeById(rowId, token);
        const fieldsToUpdate = [
          'name',
          'sector',
          'industry',
          'employees',
          'marketCap',
          'peRatio',
          'averageVolume',
          'currentPrice',
        ];

        setData((old) => {
          return old.map((row, index) => {
            if (index === rowIndex) {
              return {
                ...row,
                ...fieldsToUpdate.reduce((acc, field) => {
                  if (updatedTrade[field] !== undefined) {
                    acc[field] = updatedTrade[field];
                  }
                  return acc;
                }, {}),
              };
            }
            return row;
          });
        });
      };

      setData((old) =>
        old.map((row, index) => {
          if (index === rowIndex) {
            if (columnId === 'ticker' && rowId) {
              // updateRowWithFetchedData();
              return {
                ...old[rowIndex],
                [columnId]: value,
              };
            } else {
              return {
                ...old[rowIndex],
                [columnId]: value,
              };
            }
          }
          return row;
        })
      );
    } catch (error) {
      if (error.response.data.error === 'Invalid token.') logout();
    }
  };

  const columns = AssignedStockColumns({ handleDelete, updateData, columnVisibility, footerSums });
  const defaultColumn = {
    cell: (info) => {
      return (
        <NumberCell
          key={info.row.id}
          updateData={updateData}
          getValue={info.row.getValue}
          row={info.row}
          columnId={info.column.id}
        />
      );
    },
  };

  const table = useReactTable({
    data,
    defaultColumn,
    columns,
    state: {
      columnVisibility,
      columnOrder,
      sorting,
      globalFilter,
    },
    enableSorting: sort,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    onColumnOrderChange: setColumnOrder,
    autoResetPageIndex,
    meta: {
      updateData,
      addRow: () => {
        const newRow = createNewPortfolioTrade();
        setData((oldData) => [newRow, ...oldData]);
        setNewRow(true);
      },
    },
  });
  const [modalState, setModalState] = useState({ isOpen: false, trade: null });


  const originalDataRef = useRef([]);

  const fetchData = async () => {
    try {
      const trades = await fetchAssignedTrades(token);
      const calculatedTrades = addCalculatedFieldsToAssignedTrades(trades);
      originalDataRef.current = calculatedTrades || [];
      setData(originalDataRef.current);
    } catch (error) {
      console.error('Error fetching trades:', error);
      if (error.response.data.error === 'Invalid token.') logout();
    }
  };

  useEffect(() => {

    fetchData();
  }, [token]);






  const editableColumns = [
    'color',
    'putCall',
    'ticker',
    'tradeDate',
    'expirationDate',
    'strike',
    'tradeType',
    'premium',
    'numberOfOptions',
    'stockPriceAtTrade',
    'premiumMarketPrice',
    'closingDate',
    'closingTradeType',
    'closingPremium',
    'finalPL',
  ];

  // Render the table
  return (
    <div
      style={{ width: calculateWidth(activeMenu, screenSize) }}
      className='flex flex-col  bg-light-background p-4  h-full mx-auto '
    >
      <div className='flex w-full items-center justify-between mb-6 mt-2 gap-4'>

        <h1 className='text-3xl font-semibold  text-light-text'>
          Assigned stock positions calculator
        </h1>
        <button className='animate-scale-once mr-4 ' onClick={() => guidedTutorialHandler(guidedAssignedStocks)}><BsQuestionOctagon className='w-9 h-9 fill-orange-500' /></button>
      </div>
      <ConfirmationModalContent
        isOpen={modalState.isOpen}
        title='Delete Trade'
        message='Are you sure you want to delete this trade? This action cannot be undone.'
        onConfirm={() =>
          handleDeleteConfirm({
            modalState,
            setModalState,
            token,
            setData,
            logout,
          })
        }
        onCancel={() => {
          setModalState({ isOpen: false, trade: null });
        }}
      />

      <div id='toolbar' className="flex flex-col lg:flex-row justify-between items-center text-md mb-4 bg-gradient-to-r from-gray-200 to-gray-300 px-4 p-2 rounded-lg shadow-md transition-all duration-300 ease-in-out" >

        <SearchAndColumnToggle
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          contextMenuState={contextMenuState}
          setContextMenuState={setContextMenuState}
          table={table}
        />
        <TableActionControls
          newRow={newRow}
          handlePortfolioSave={handlePortfolioSave}
          data={data}
          token={token}
          setData={setData}
          setNewRow={setNewRow}
          setSort={setSort}
          logout={logout}
          fetchData={fetchData}
          setColorsKeyMapState={setColorsKeyMapState}
          colorsKeyMapState={colorsKeyMapState}
          table={table}
        />

      </div>

      <div className='my-2 overflow-x-auto'>
        <div className='py-1 align-middle inline-block '>
          <div className='shadow  border-b border-gray-200  sm:rounded-2xl overflow-hidden'>
            <div className=' rounded-t-3xl'>
              <table
                id='tableContainer'
                style={{
                  width: table.getCenterTotalSize(),
                }}
                className=' divide-y  divide-gray-200 table'
              >
                <TableHeader
                  headerGroups={table.getHeaderGroups()}
                  screenSize={screenSize} // This value should come from your state or context
                  calculateLeftOffset={calculateLeftOffset} // This function should be defined or imported
                  columnVisibility={columnVisibility} // This state should be managed in the parent component
                  newRow={newRow} // This state should be managed in the parent component
                  editableColumns={editableColumns} // This array should be defined or imported
                  currentColor={currentColor} // This value should come from your state or context
                />
                <TableBody
                  data={data}
                  table={table}
                  screenSize={screenSize} // Pass the screenSize state
                  // getTradeStatus={getTradeStatus} // Pass the getTradeStatus function
                  newRow={newRow} // Pass the newRow state
                />
                <TableFooter
                  footerGroups={table.getFooterGroups()}
                />
              </table>
            </div>
          </div>
        </div>
      </div>
    </div >
  );
}

export default AssignedStock